<template>
    <div id="sms-log-container">
        <card title="SMS Conversation Log">
            <b-form class="filter-grid mb-3" @submit.prevent="searchConversations">
                <b-row>
                    <b-col>
                        <SearchComponent v-model="userFilter" label="Agent" :option-label="userLabel"
                            search-u-r-l="/api/SmsLog/GetAllAgents" prepopulate clearable />
                    </b-col>
                    <b-col>
                        <fg-input v-model="externalNumber" label="Phone Number:" />
                    </b-col>
                    <b-col>
                        <fg-input v-model="conversationSidFilter" label="Conversation SID:" />
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="patientFilter" label="Patient" :option-label="patientLabel"
                            search-u-r-l="/Patient" clearable />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <div>
                            <label>Conversation Date:</label>
                            <div class="d-flex" style="gap: 15px;">
                                <el-date-picker size="sm" v-model="startDate" today-button reset-button
                                    format="MM/dd/yyyy" type="date" />
                                <el-date-picker size="sm" v-model="endDate" today-button reset-button
                                    format="MM/dd/yyyy" type="date" />
                            </div>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button type="submit" variant="primary">Search</b-button>
                    </b-col>
                </b-row>
            </b-form>
        </card>
        <card>
            <b-table id="SMSLog" striped responsive="true" sort-icon-left :items="conversations" :fields="fields">
                <template #cell(expandToggle)="data">
                    <div @click="selectConversation(data)" style="cursor: pointer; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
                        <i :class="{ 'fa fa-fw fa-chevron-right text-primary': true, 'fa-rotate-90': data.detailsShowing }"
                            style="transition: transform .15s;"></i>
                    </div>
                </template>
                <template #cell(patient)="data">
                    <router-link v-if="data.item.patientId && (data.item.patientFirstName || data.item.patientLastName)"
                        :to="`/processing/patient-dashboard/${data.item.patientId}`">
                        {{ data.item.patientFirstName }} {{ data.item.patientLastName }}
                    </router-link>
                </template>
                <template #row-details="row">
                    <card plain class="bg-white">
                        <div class="mb-3">
                            <strong>Conversation SID:</strong> {{ row.item.conversationSid }}
                        </div>
                        <h6 style="font-weight: 500">Conversation Messages</h6>
                        <div class="message-container">
                            <div v-for="(msg, idx) in parseMessages(row.item.messages)" :key="idx"
                                :class="['message-bubble', msg.isInbound ? 'inbound' : 'outbound']">
                                <div class="message-header">
                                    <span class="message-author">{{ formatAuthor(msg.author, msg.isInbound) }}</span>
                                    <span class="message-time">{{ formatMessageTime(msg.dateCreated) }}</span>
                                </div>
                                <div class="message-body">{{ msg.body }}</div>
                            </div>
                            <div v-if="!row.item.messages || parseMessages(row.item.messages).length === 0"
                                class="text-muted">
                                No messages available
                            </div>
                        </div>
                    </card>
                </template>
            </b-table>

            <BottomPagerBar hide-pager no-stick item-name="conversation" :total-rows="conversations.length" show-total />
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import SearchComponent from '@/components/SearchComponent.vue';
import { DatePicker } from "element-ui";
import Axios from "axios";
import { SmsLog, SmsMessage } from '@/models/SmsLog';
import { Patient } from '@/models/Patient';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import BottomPagerBar from '@/components/BottomPagerBar.vue';

@Component({
    name: "SMSLog",
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        BottomPagerBar,
    }
})
export default class SMSLogPage extends Vue {
    protected startDate?: string = moment().add(-30, 'day').toISOString();
    protected endDate?: string = moment().toISOString();
    protected externalNumber: string = "";
    protected conversationSidFilter: string = "";
    protected patientFilter: Patient | null = null;
    protected isLoading?: boolean = false;
    protected userFilter: SmsLog | null = null;
    protected conversations: Array<SmsLog> = [];
    protected fields: any[] = [
        { key: "expandToggle", label: "" },
        { key: "conversationSid", label: "Conversation SID", sortable: true },
        { key: "externalNumber", label: "Phone Number", sortable: true },
        { key: "agent", label: "Agent", sortable: true },
        { key: "patient", label: "Patient", sortable: true },
        { key: "initiated", label: "Started", sortable: true, formatter: (d: Date) => moment.utc(d).local().format("L LT") },
        { key: "completed", label: "Completed", sortable: true, formatter: (d: Date) => d ? moment.utc(d).local().format("L LT") : '-' },
        { key: "messageCount", label: "Messages", sortable: true },
    ];

    userLabel(c: any) {
        return c?.agent ? `${c.agent}` : '';
    }

    patientLabel(p: any) {
        return p?.id ? `ID: ${p.id} ${p.lastName}, ${p.firstName}` : ''
    }

    mounted() {
        const phone = this.$route.query["phone"] as string;
        if (phone) {
            this.externalNumber = phone.replace("+", "").trim();
            this.searchConversations();
        }
    }

    async searchConversations() {
        try {
            this.isLoading = true;
            const response = await Axios.get('/api/SmsLog', {
                params: {
                    startDate: this.startDate,
                    endDate: this.endDate,
                    externalNumber: this.externalNumber || null,
                    agent: (this.userFilter as any)?.agent || null,
                    patientId: this.patientFilter?.id || null,
                    conversationSid: this.conversationSidFilter || null
                }
            });
            this.conversations = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    selectConversation(r: any) {
        r.toggleDetails();
    }

    parseMessages(messagesJson: string | null | undefined): SmsMessage[] {
        if (!messagesJson) {
            return [];
        }
        try {
            const parsed = JSON.parse(messagesJson) as any[];
            // Normalize property names (handle both PascalCase and camelCase)
            return parsed.map(msg => ({
                sid: msg.sid ?? msg.Sid,
                author: msg.author ?? msg.Author,
                body: msg.body ?? msg.Body,
                dateCreated: msg.dateCreated ?? msg.DateCreated,
                index: msg.index ?? msg.Index,
                isInbound: msg.isInbound ?? msg.IsInbound
            }));
        } catch {
            return [];
        }
    }

    formatAuthor(author: string | undefined, isInbound: boolean): string {
        if (!author) return isInbound ? 'Customer' : 'Agent';
        // If it's a phone number, format it
        if (/^\+?\d+$/.test(author.replace(/[\s\-()]/g, ''))) {
            return 'Customer';
        }
        return author;
    }

    formatMessageTime(dateCreated: Date | string | undefined): string {
        if (!dateCreated) return '';
        return moment.utc(dateCreated).local().format("LT");
    }
}
</script>

<style scoped>
.message-container {
    max-height: 400px;
    overflow-y: auto;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 8px;
}

.message-bubble {
    max-width: 70%;
    margin-bottom: 10px;
    padding: 10px 14px;
    border-radius: 12px;
    clear: both;
}

.message-bubble.inbound {
    background-color: #e9ecef;
    float: left;
    border-bottom-left-radius: 4px;
}

.message-bubble.outbound {
    background-color: #007bff;
    color: white;
    float: right;
    border-bottom-right-radius: 4px;
}

.message-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    margin-bottom: 4px;
    opacity: 0.8;
}

.message-author {
    font-weight: 600;
}

.message-body {
    word-wrap: break-word;
}

.message-bubble.outbound .message-header {
    color: rgba(255, 255, 255, 0.9);
}
</style>
